import React from 'react';
import { Box, Paper, Typography, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@emotion/react';
import { tokens } from '../theme';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import UpdateIcon from '@mui/icons-material/Update';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const UnitCard = ({
  unit,
  onEdit,
  onDelete,
  onClick
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { 
    code = '', 
    title = '',
    unit_code = '',
    specific_unit = '',
    unit_id,
    createdAt,
    updatedAt,
    skills = []
  } = unit || {};
  
  // Use API fields if available, otherwise use mapped fields
  const displayCode = unit_code || code;
  const displayTitle = specific_unit || title;
  
  // const skillCount = skills?.length || 0;

  // Format dates
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, { 
        year: 'numeric',
        month: 'short', 
        day: 'numeric',
      });
    } catch (error) {
      return '';
    }
  };
  
  const createdDate = formatDate(createdAt);
  const updatedDate = formatDate(updatedAt);

  return (
    <Paper 
      elevation={0}
      sx={{
        p: 1,
        backgroundColor:"background.secondCard",
        borderRadius: '8px',
        width: '100%',
        transition: 'all 0.2s ease-in-out',
    
        border: theme.palette.mode === 'dark' ? `1px solid rgba(255, 255, 255, 0.1)` : `1px solid ${colors.grey[300]}`,
        cursor: 'pointer',
      }}
      onClick={() => onClick && onClick(unit)}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center', 
            pt: 0.5
          }}>
            <DescriptionOutlinedIcon
              sx={{ 
                color: colors.grey[500],
                fontSize: '24px',
                '&:hover': { color: colors.primary[400] } 
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.grey[800] }}>
              {displayCode && <span style={{ marginRight: '6px' }}>{displayCode}</span>}
              {displayCode && displayTitle && <span>-</span>}
              {displayTitle && <span style={{ marginLeft: '6px' }}>{displayTitle}</span>}
            </Typography>
            {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" sx={{ color: colors.grey[500], fontWeight: 500 }}>
                {skillCount} skill{skillCount !== 1 ? 's' : ''}
              </Typography>
            </Box> */}
            {(createdDate || updatedDate) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {createdDate && (
                  <>
                    <AccessTimeIcon sx={{ 
                      fontSize: '14px', 
                      color: colors.grey[500],
                    }} />
                    <Typography variant="caption" sx={{ color: colors.grey[500] }}>
                      {createdDate}
                    </Typography>
                  </>
                )}
                {createdDate && updatedDate && (
                  <FiberManualRecordIcon sx={{ 
                    fontSize: '6px', 
                    color: colors.grey[400],
                    mx: 0.5
                  }} />
                )}
                {updatedDate && (
                  <>
                    <UpdateIcon sx={{ 
                      fontSize: '14px', 
                      color: colors.grey[500],
                    }} />
                    <Typography variant="caption" sx={{ color: colors.grey[500] }}>
                      {updatedDate}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex' }}>
          <Tooltip title="Edit unit">
            <IconButton 
              onClick={(e) => {
                e.stopPropagation();
                if (onEdit) onEdit(unit);
              }} 
              size="small"
            >
              <EditIcon sx={{ 
                fontSize: '20px',
                color: colors.grey[650],
                '&:hover': { color: colors.primary[500] } 
              }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete unit">
            <IconButton 
              onClick={(e) => {
                e.stopPropagation();
                if (onDelete) onDelete(unit);
              }} 
              size="small"
            >
              <DeleteIcon sx={{ 
                fontSize: '20px',
                color: colors.grey[650],
                '&:hover': {color: colors.redAccent[500] } 
              }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
};

export default UnitCard;
